import { useEffect, useState } from 'react';
import { Button, Modal, Select, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { addCollaborationCreators, getCollaborationPage } from '../../../store/request';

type InviteOption = {
  value: number;
  label: string;
};

type BatchInviteModalProps = {
  open: boolean;
  shopId?: string | null;
  influencerIds: string[];
  title?: string;
  onClose: () => void;
  onSuccess?: () => void;
};

export function BatchInviteModal({ open, shopId, influencerIds, title, onClose, onSuccess }: BatchInviteModalProps) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<InviteOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [collaborationIds, setCollaborationIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const currentShopId = shopId?.trim() || '';

  useEffect(() => {
    if (!open) return;
    setCollaborationIds([]);
    if (!currentShopId) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setOptionsLoading(true);
    getCollaborationPage({ shopId: currentShopId, pageNum: 1, pageSize: 50 })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('invitation.invite_options_failed'));
          setOptions([]);
          return;
        }
        const next = (res.content?.records ?? [])
          .filter((row) => row.id != null)
          .map((row) => ({
            value: row.id as number,
            label: row.shopName ? `${row.name || '—'} · ${row.shopName}` : row.name || String(row.id),
          }));
        setOptions(next);
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentShopId, t]);

  const submit = async () => {
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    if (collaborationIds.length === 0 || collaborationIds.length > 10) {
      message.warning(t('invitation.invite_pick_required'));
      return;
    }
    if (influencerIds.length === 0) {
      message.warning(t('talent.select_required'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await addCollaborationCreators({
        shopId: currentShopId,
        collaborationIds,
        influencerIds,
      });
      if (res.code !== 200) {
        message.error(res.message || t('invitation.invite_failed'));
        return;
      }
      message.success(res.message || t('invitation.invite_success'));
      onSuccess?.();
      onClose();
    } catch {
      message.error(t('invitation.invite_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={title || t('talent.batch_invite')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={520}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="primary"
            className="wb-btn-primary"
            loading={submitting}
            disabled={collaborationIds.length === 0 || influencerIds.length === 0}
            onClick={submit}
          >
            {t('invitation.add')}
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-3">
        <div className="w-16 shrink-0 text-right text-sm font-medium text-slate-700">{t('invitation.invite')}</div>
        <Select
          className="min-w-0 flex-1"
          mode="multiple"
          maxCount={10}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t('invitation.invite_pick_required')}
          loading={optionsLoading}
          value={collaborationIds}
          options={options}
          onChange={setCollaborationIds}
        />
      </div>
      <p className="mb-0 mt-3 text-xs leading-relaxed text-slate-400">{t('invitation.batch_invite_hint')}</p>
    </Modal>
  );
}
